'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, useContext, useEffect, useEffectEvent, useRef, useState } from 'react';
import { AUTH_SESSION_MAX_AGE, clearTokenCookie, getTokenFromCookie, setTokenCookie } from './cookies';
import { AuthRefreshCancelledError } from './fetch';
import { fetchAuthUser } from './fetch-user';
import { clearImpersonationRecovery, getAdminToken, getImpersonationRecovery, isImpersonationRecoveryAdminTokenValid, isImpersonationRecoveryBindingValid } from './impersonation-recovery';
import { refreshAuthToken } from './refresh-token';
import { getImpersonatorId, getTokenExpiration, getTokenSubject, isImpersonationToken, isTokenExpired } from './token';
import type { User, UserContactsData } from './types';
interface AuthContextValue {
  apiUrl: string;
  getSessionToken: () => null | string;
  getSessionVersion: () => number;
  impersonatorId: null | number | string;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  isLoading: boolean;
  login: (token: string, expiresIn?: number) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<null | string>;
  refreshUser: () => Promise<void>;
  replaceAuthToken: (token: string, expiresIn?: number) => Promise<void>;
  restoreImpersonationSession: (expectedSessionVersion?: number) => Promise<boolean>;
  source: string;
  token: null | string;
  user: null | User;
  userContacts: null | UserContactsData;
}
const AuthContext = createContext<AuthContextValue | null>(null);
interface AuthProviderProps {
  apiUrl: string;
  children: ReactNode;
  initialToken?: null | string;
  initialUser?: null | User;
  initialUserContacts?: null | UserContactsData;
  source: string;
}
type AuthSessionOwner = {
  token: null | string;
  version: number;
};
type RefreshOperation = {
  operation: object;
  owner: AuthSessionOwner;
  promise: Promise<null | string>;
};
interface UseRestoreImpersonationSessionOptions {
  beginSession: () => number;
  clearAuth: (owner?: AuthSessionOwner) => boolean;
  fetchUser: (authToken: string) => Promise<null | User>;
  getCurrentToken: () => null | string;
  getSessionVersion: () => number;
  isCurrentSession: (version: number, token?: null | string) => boolean;
  setToken: (token: null | string) => void;
  setUser: (user: null | User) => void;
}
export function AuthProvider(t0) {
  const $ = _c(54);
  const {
    apiUrl,
    children,
    initialToken,
    initialUser,
    initialUserContacts,
    source
  } = t0;
  const [token, setTokenState] = useState(initialToken ?? null);
  const [user, setUser] = useState(initialUser ?? null);
  const [userContacts, setUserContacts] = useState(initialUserContacts ?? null);
  const [isLoading, setIsLoading] = useState(!initialToken);
  const refreshOperationRef = useRef(null);
  const sessionVersionRef = useRef(0);
  const tokenRef = useRef(initialToken ?? null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = nextToken => {
      tokenRef.current = nextToken;
      setTokenState(nextToken);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const setToken = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => sessionVersionRef.current;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const getSessionVersion = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => tokenRef.current;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const getSessionToken = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => tokenRef.current ?? getTokenFromCookie();
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const getCurrentToken = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (version, expectedToken) => {
      if (version !== sessionVersionRef.current) {
        return false;
      }
      if (expectedToken === undefined) {
        return true;
      }
      const cookieToken = getTokenFromCookie();
      return expectedToken === cookieToken && (tokenRef.current === null || tokenRef.current === expectedToken);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const isCurrentSession = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      sessionVersionRef.current = sessionVersionRef.current + 1;
      refreshOperationRef.current = null;
      setUser(null);
      setUserContacts(null);
      return sessionVersionRef.current;
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const beginSession = t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = owner => {
      if (owner && !isCurrentSession(owner.version, owner.token)) {
        return false;
      }
      clearTokenCookie();
      clearImpersonationRecovery();
      setToken(null);
      setUser(null);
      setUserContacts(null);
      return true;
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const clearAuth = t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const ownToken = tokenRef.current;
      const cookieToken_0 = getTokenFromCookie();
      if (!ownToken || !cookieToken_0 || cookieToken_0 === ownToken || isImpersonationToken(ownToken) || isImpersonationToken(cookieToken_0) || getTokenSubject(cookieToken_0) !== getTokenSubject(ownToken)) {
        return null;
      }
      setToken(cookieToken_0);
      return cookieToken_0;
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const adoptCookieToken = t8;
  let t9;
  if ($[8] !== apiUrl || $[9] !== source) {
    t9 = {
      apiUrl,
      source
    };
    $[8] = apiUrl;
    $[9] = source;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const fetchUser = useFetchUser(t9);
  let t10;
  if ($[11] !== apiUrl || $[12] !== source) {
    t10 = {
      adoptCookieToken,
      apiUrl,
      clearAuth,
      getCurrentToken,
      getSessionVersion,
      isCurrentSession,
      refreshOperationRef,
      setToken,
      source
    };
    $[11] = apiUrl;
    $[12] = source;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const refreshToken = useRefreshToken(t10);
  let t11;
  if ($[14] !== fetchUser) {
    t11 = {
      beginSession,
      clearAuth,
      fetchUser,
      getCurrentToken,
      getSessionVersion,
      isCurrentSession,
      setToken,
      setUser
    };
    $[14] = fetchUser;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const restoreImpersonationSession = useRestoreImpersonationSession(t11);
  let t12;
  if ($[16] !== fetchUser || $[17] !== initialToken || $[18] !== refreshToken || $[19] !== restoreImpersonationSession) {
    t12 = {
      fetchUser,
      getSessionVersion,
      initialToken,
      isCurrentSession,
      refreshToken,
      restoreImpersonationSession,
      setIsLoading,
      setToken,
      setUser
    };
    $[16] = fetchUser;
    $[17] = initialToken;
    $[18] = refreshToken;
    $[19] = restoreImpersonationSession;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  useInitializeAuth(t12);
  const t13 = user !== null;
  let t14;
  if ($[21] !== refreshToken || $[22] !== t13 || $[23] !== token) {
    t14 = {
      adoptCookieToken,
      isSignedIn: t13,
      refreshToken,
      token
    };
    $[21] = refreshToken;
    $[22] = t13;
    $[23] = token;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  useKeepTokenFresh(t14);
  let t15;
  if ($[25] !== fetchUser) {
    t15 = {
      beginSession,
      fetchUser,
      getSessionVersion,
      isCurrentSession,
      setToken,
      setUser
    };
    $[25] = fetchUser;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const login = useLogin(t15);
  let t16;
  if ($[27] !== apiUrl) {
    t16 = {
      apiUrl,
      beginSession,
      clearAuth,
      getCurrentToken
    };
    $[27] = apiUrl;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const logout = useLogout(t16);
  let t17;
  if ($[29] !== fetchUser) {
    t17 = {
      fetchUser,
      getCurrentToken,
      getSessionVersion,
      isCurrentSession,
      setUser
    };
    $[29] = fetchUser;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const refreshUser = useRefreshUser(t17);
  let t18;
  if ($[31] !== fetchUser) {
    t18 = {
      beginSession,
      fetchUser,
      getSessionVersion,
      isCurrentSession,
      setToken,
      setUser
    };
    $[31] = fetchUser;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const replaceAuthToken = useReplaceAuthToken(t18);
  let t19;
  if ($[33] !== token) {
    t19 = token ? getImpersonatorId(token) : null;
    $[33] = token;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const impersonatorId = t19;
  const isImpersonating = impersonatorId !== null;
  const t20 = !!token && !!user;
  let t21;
  if ($[35] !== apiUrl || $[36] !== impersonatorId || $[37] !== isImpersonating || $[38] !== isLoading || $[39] !== login || $[40] !== logout || $[41] !== refreshToken || $[42] !== refreshUser || $[43] !== replaceAuthToken || $[44] !== restoreImpersonationSession || $[45] !== source || $[46] !== t20 || $[47] !== token || $[48] !== user || $[49] !== userContacts) {
    t21 = {
      apiUrl,
      getSessionToken,
      getSessionVersion,
      impersonatorId,
      isAuthenticated: t20,
      isImpersonating,
      isLoading,
      login,
      logout,
      refreshToken,
      refreshUser,
      replaceAuthToken,
      restoreImpersonationSession,
      source,
      token,
      user,
      userContacts
    };
    $[35] = apiUrl;
    $[36] = impersonatorId;
    $[37] = isImpersonating;
    $[38] = isLoading;
    $[39] = login;
    $[40] = logout;
    $[41] = refreshToken;
    $[42] = refreshUser;
    $[43] = replaceAuthToken;
    $[44] = restoreImpersonationSession;
    $[45] = source;
    $[46] = t20;
    $[47] = token;
    $[48] = user;
    $[49] = userContacts;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== children || $[52] !== t21) {
    t22 = <AuthContext.Provider value={t21} data-sentry-element="AuthContext.Provider" data-sentry-component="AuthProvider" data-sentry-source-file="context.tsx">{children}</AuthContext.Provider>;
    $[51] = children;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  return t22;
}
export function createImpersonationSessionRestorer({
  beginSession,
  clearAuth,
  fetchUser,
  getCurrentToken,
  getSessionVersion,
  isCurrentSession,
  setToken,
  setUser
}: UseRestoreImpersonationSessionOptions) {
  let pending: null | {
    operation: object;
    owner: AuthSessionOwner;
    promise: Promise<boolean>;
  } = null;
  return (expectedSessionVersion?: number): Promise<boolean> => {
    if (expectedSessionVersion !== undefined && expectedSessionVersion !== getSessionVersion()) {
      return Promise.resolve(false);
    }
    if (pending && pending.owner.version === getSessionVersion()) {
      return pending.promise;
    }
    const owner: AuthSessionOwner = {
      token: getCurrentToken(),
      version: beginSession()
    };
    const operation = {};
    const promise = (async () => {
      const isOwner = (expectedToken?: null | string) => {
        return isCurrentSession(owner.version, expectedToken);
      };
      const clearOwner = (expectedToken: null | string) => {
        return clearAuth({
          token: expectedToken,
          version: owner.version
        });
      };
      try {
        if (typeof window === 'undefined') return false;
        const recovery = getImpersonationRecovery();
        if (!recovery) {
          clearOwner(owner.token);
          return false;
        }
        const now = Date.now();
        if (owner.token && !isImpersonationRecoveryBindingValid(recovery, owner.token)) {
          clearOwner(owner.token);
          return false;
        }
        if (!isImpersonationRecoveryAdminTokenValid(recovery, now)) {
          clearOwner(owner.token);
          return false;
        }
        const adminToken = getAdminToken(recovery);
        if (isTokenExpired(adminToken, now)) {
          clearOwner(owner.token);
          return false;
        }
        if (!isOwner(owner.token)) return false;
        clearImpersonationRecovery();
        setTokenCookie(adminToken);
        setToken(adminToken);
        const userData = await fetchUser(adminToken);
        if (!isOwner(adminToken)) return false;
        if (userData) {
          setUser(userData);
          return true;
        }
        clearOwner(adminToken);
        return false;
      } finally {
        if (pending?.operation === operation) {
          pending = null;
        }
      }
    })();
    pending = {
      operation,
      owner,
      promise
    };
    return promise;
  };
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

/**
 * Compatibility shim for useSession - allows gradual migration.
 * @deprecated Use useAuth() instead.
 */
export function useSession() {
  const $ = _c(10);
  const {
    isLoading,
    refreshUser,
    token,
    user,
    userContacts
  } = useAuth();
  let t0;
  if ($[0] !== token || $[1] !== user || $[2] !== userContacts) {
    t0 = user ? {
      user: {
        ...user,
        access_token: token
      },
      userContacts
    } : null;
    $[0] = token;
    $[1] = user;
    $[2] = userContacts;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const t1 = isLoading ? "loading" : user ? "authenticated" : "unauthenticated";
  let t2;
  if ($[4] !== refreshUser) {
    t2 = async _data => {
      await refreshUser();
    };
    $[4] = refreshUser;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      data: t0,
      status: t1,
      update: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function commitAuthToken({
  fetchUser,
  getSessionVersion,
  isCurrentSession,
  setToken,
  setUser
}: {
  fetchUser: (authToken: string) => Promise<null | User>;
  getSessionVersion: () => number;
  isCurrentSession: (version: number, token?: null | string) => boolean;
  setToken: (token: null | string) => void;
  setUser: (user: null | User) => void;
}) {
  return async (newToken: string, expiresIn: number | undefined, sessionVersion: number) => {
    if (sessionVersion !== getSessionVersion()) return;
    clearTokenCookie();
    // Refreshable tokens keep the cookie for the refresh window; impersonation tokens cannot be refreshed.
    setTokenCookie(newToken, isImpersonationToken(newToken) ? expiresIn : AUTH_SESSION_MAX_AGE);
    setToken(newToken);
    setUser(null);
    const userData = await fetchUser(newToken);
    if (!isCurrentSession(sessionVersion, newToken)) return;
    setUser(userData);
  };
}

/** Backend invalidation is best effort after local auth is already cleared. */
function ignoreLogoutInvalidationError(_error: unknown) {
  return undefined;
}
function useFetchUser({
  apiUrl,
  source
}: {
  apiUrl: string;
  source: string;
}) {
  return async (authToken: string): Promise<null | User> => {
    return fetchAuthUser({
      apiUrl,
      source,
      token: authToken
    });
  };
}
function useInitializeAuth(t0) {
  const $ = _c(14);
  const {
    fetchUser,
    getSessionVersion,
    initialToken,
    isCurrentSession,
    refreshToken,
    restoreImpersonationSession,
    setIsLoading,
    setToken,
    setUser
  } = t0;
  const initializedRef = useRef(false);
  const fetchLatestUser = useEffectEvent(fetchUser);
  const refreshLatestToken = useEffectEvent(refreshToken);
  const restoreLatestSession = useEffectEvent(restoreImpersonationSession);
  const setLatestIsLoading = useEffectEvent(setIsLoading);
  const setLatestToken = useEffectEvent(setToken);
  const setLatestUser = useEffectEvent(setUser);
  let t1;
  if ($[0] !== fetchLatestUser || $[1] !== getSessionVersion || $[2] !== initialToken || $[3] !== isCurrentSession || $[4] !== refreshLatestToken || $[5] !== restoreLatestSession || $[6] !== setLatestIsLoading || $[7] !== setLatestToken || $[8] !== setLatestUser) {
    t1 = () => {
      if (initializedRef.current || initialToken) {
        return;
      }
      initializedRef.current = true;
      const cookieToken = getTokenFromCookie();
      const initialSessionVersion = getSessionVersion();
      let loadingSessionVersion = initialSessionVersion;
      const ownsLoadingSession = () => loadingSessionVersion === getSessionVersion();
      const finishLoading = () => {
        if (ownsLoadingSession()) {
          setLatestIsLoading(false);
        }
      };
      const finishWithReload = () => {
        if (!ownsLoadingSession()) {
          return;
        }
        setLatestIsLoading(false);
        if (typeof window !== "undefined") {
          window.location.reload();
        }
      };
      const attemptRestore = async tokenWasImpersonation => {
        if (initialSessionVersion !== getSessionVersion()) {
          return;
        }
        const restoration = restoreLatestSession(initialSessionVersion);
        const recoverySessionVersion = getSessionVersion();
        loadingSessionVersion = recoverySessionVersion;
        const restored = await restoration;
        if (recoverySessionVersion !== getSessionVersion()) {
          return;
        }
        if (restored) {
          finishWithReload();
          return;
        }
        if (tokenWasImpersonation && typeof window !== "undefined") {
          window.location.href = "/";
        }
      };
      const initialize = async () => {
        if (!cookieToken) {
          await attemptRestore(false);
          return;
        }
        if (initialSessionVersion !== getSessionVersion()) {
          return;
        }
        setLatestToken(cookieToken);
        const userData = await fetchLatestUser(cookieToken);
        if (!isCurrentSession(initialSessionVersion, cookieToken)) {
          return;
        }
        if (userData) {
          setLatestUser(userData);
          return;
        }
        if (!isImpersonationToken(cookieToken)) {
          const newToken = await refreshLatestToken();
          if (initialSessionVersion !== getSessionVersion()) {
            return;
          }
          if (newToken) {
            const refreshedUser = await fetchLatestUser(newToken);
            if (!isCurrentSession(initialSessionVersion, newToken)) {
              return;
            }
            if (refreshedUser) {
              setLatestUser(refreshedUser);
            }
          }
          return;
        }
        if (!isTokenExpired(cookieToken)) {
          return;
        }
        await attemptRestore(true);
      };
      initialize().catch(() => {
        finishLoading();
      }).finally(finishLoading);
    };
    $[0] = fetchLatestUser;
    $[1] = getSessionVersion;
    $[2] = initialToken;
    $[3] = isCurrentSession;
    $[4] = refreshLatestToken;
    $[5] = restoreLatestSession;
    $[6] = setLatestIsLoading;
    $[7] = setLatestToken;
    $[8] = setLatestUser;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== getSessionVersion || $[11] !== initialToken || $[12] !== isCurrentSession) {
    t2 = [getSessionVersion, initialToken, isCurrentSession];
    $[10] = getSessionVersion;
    $[11] = initialToken;
    $[12] = isCurrentSession;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  useEffect(t1, t2);
}

/** Seconds before expiry at which an open tab refreshes its token. */
const REFRESH_AHEAD_SECONDS = 60;
/** Spreads tabs holding the same token, so the first refresh lands before the others start. */
const REFRESH_JITTER_MS = 30_000;
// setTimeout fires at once for delays above 2^31 - 1 ms.
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

/**
 * Keeps a signed-in tab on a live token: it adopts a token another tab or the proxy
 * refreshed, and otherwise refreshes shortly before expiry. It checks again when the
 * tab becomes visible, because timers fire late after sleep.
 */
function useKeepTokenFresh(t0) {
  const $ = _c(8);
  const {
    adoptCookieToken,
    isSignedIn,
    refreshToken,
    token
  } = t0;
  const adoptLatestCookieToken = useEffectEvent(adoptCookieToken);
  const refreshLatestToken = useEffectEvent(refreshToken);
  let t1;
  if ($[0] !== adoptLatestCookieToken || $[1] !== isSignedIn || $[2] !== refreshLatestToken || $[3] !== token) {
    t1 = () => {
      const exp = token ? getTokenExpiration(token) : null;
      if (!token || !isSignedIn || exp === null || isImpersonationToken(token)) {
        return;
      }
      const jitterMs = Math.random() * REFRESH_JITTER_MS;
      const msUntilRefresh = () => (exp - REFRESH_AHEAD_SECONDS) * 1000 - jitterMs - Date.now();
      const keepFresh = () => {
        if (adoptLatestCookieToken() || msUntilRefresh() > 0) {
          return;
        }
        refreshLatestToken().catch(_temp);
      };
      const onVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          keepFresh();
        }
      };
      const timeoutId = setTimeout(keepFresh, Math.min(msUntilRefresh(), MAX_TIMER_DELAY_MS));
      document.addEventListener("visibilitychange", onVisibilityChange);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };
    };
    $[0] = adoptLatestCookieToken;
    $[1] = isSignedIn;
    $[2] = refreshLatestToken;
    $[3] = token;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== isSignedIn || $[6] !== token) {
    t2 = [isSignedIn, token];
    $[5] = isSignedIn;
    $[6] = token;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useEffect(t1, t2);
}
function _temp() {
  return null;
}
function useLogin({
  beginSession,
  fetchUser,
  getSessionVersion,
  isCurrentSession,
  setToken,
  setUser
}: {
  beginSession: () => number;
  fetchUser: (authToken: string) => Promise<null | User>;
  getSessionVersion: () => number;
  isCurrentSession: (version: number, token?: null | string) => boolean;
  setToken: (token: null | string) => void;
  setUser: (user: null | User) => void;
}) {
  const commit = commitAuthToken({
    fetchUser,
    getSessionVersion,
    isCurrentSession,
    setToken,
    setUser
  });
  return async (newToken: string, expiresIn?: number) => {
    const sessionVersion = beginSession();
    clearImpersonationRecovery();
    await commit(newToken, expiresIn, sessionVersion);
  };
}
function useLogout({
  apiUrl,
  beginSession,
  clearAuth,
  getCurrentToken
}: {
  apiUrl: string;
  beginSession: () => number;
  clearAuth: (owner?: AuthSessionOwner) => boolean;
  getCurrentToken: () => null | string;
}) {
  return async () => {
    const currentToken = getCurrentToken();
    const sessionVersion = beginSession();
    clearAuth({
      token: currentToken,
      version: sessionVersion
    });
    if (currentToken) {
      try {
        await fetch(`${apiUrl}/auth/logout`, {
          headers: {
            Authorization: `Bearer ${currentToken}`
          },
          method: 'POST'
        });
      } catch (error) {
        ignoreLogoutInvalidationError(error);
      }
    }
  };
}
function useRefreshToken({
  adoptCookieToken,
  apiUrl,
  clearAuth,
  getCurrentToken,
  getSessionVersion,
  isCurrentSession,
  refreshOperationRef,
  setToken,
  source
}: {
  adoptCookieToken: () => null | string;
  apiUrl: string;
  clearAuth: (owner?: AuthSessionOwner) => boolean;
  getCurrentToken: () => null | string;
  getSessionVersion: () => number;
  isCurrentSession: (version: number, token?: null | string) => boolean;
  refreshOperationRef: {
    current: null | RefreshOperation;
  };
  setToken: (token: null | string) => void;
  source: string;
}) {
  return async (): Promise<null | string> => {
    // Refreshing the token this tab holds would fail once another tab or the proxy has rotated it.
    const adoptedToken = adoptCookieToken();
    if (adoptedToken && !isTokenExpired(adoptedToken)) return adoptedToken;
    const currentToken = getCurrentToken();
    if (!currentToken || isImpersonationToken(currentToken)) return null;
    const owner: AuthSessionOwner = {
      token: currentToken,
      version: getSessionVersion()
    };
    const existingOperation = refreshOperationRef.current;
    if (existingOperation && existingOperation.owner.version === owner.version && existingOperation.owner.token === owner.token) {
      return existingOperation.promise;
    }
    const operation = {};
    const promise = (async () => {
      try {
        const result = await refreshAuthToken({
          apiUrl,
          source,
          token: currentToken
        });
        if (!isCurrentSession(owner.version, owner.token)) {
          throw new AuthRefreshCancelledError();
        }
        if (result.status === 'rejected') {
          if (!clearAuth(owner)) {
            throw new AuthRefreshCancelledError();
          }
          return null;
        }
        if (result.status === 'unavailable') {
          throw new Error('Token refresh is unavailable');
        }
        setTokenCookie(result.token);
        setToken(result.token);
        if (!isCurrentSession(owner.version, result.token)) {
          throw new AuthRefreshCancelledError();
        }
        return result.token;
      } finally {
        if (refreshOperationRef.current?.operation === operation) {
          refreshOperationRef.current = null;
        }
      }
    })();
    refreshOperationRef.current = {
      operation,
      owner,
      promise
    };
    return promise;
  };
}
function useRefreshUser({
  fetchUser,
  getCurrentToken,
  getSessionVersion,
  isCurrentSession,
  setUser
}: {
  fetchUser: (authToken: string) => Promise<null | User>;
  getCurrentToken: () => null | string;
  getSessionVersion: () => number;
  isCurrentSession: (version: number, token?: null | string) => boolean;
  setUser: (user: null | User) => void;
}) {
  return async () => {
    const currentToken = getCurrentToken();
    const sessionVersion = getSessionVersion();
    if (!currentToken) return;
    const userData = await fetchUser(currentToken);
    if (!isCurrentSession(sessionVersion, currentToken)) return;
    if (userData) {
      setUser(userData);
    }
  };
}
function useReplaceAuthToken({
  beginSession,
  fetchUser,
  getSessionVersion,
  isCurrentSession,
  setToken,
  setUser
}: {
  beginSession: () => number;
  fetchUser: (authToken: string) => Promise<null | User>;
  getSessionVersion: () => number;
  isCurrentSession: (version: number, token?: null | string) => boolean;
  setToken: (token: null | string) => void;
  setUser: (user: null | User) => void;
}) {
  const commit = commitAuthToken({
    fetchUser,
    getSessionVersion,
    isCurrentSession,
    setToken,
    setUser
  });
  return async (newToken: string, expiresIn?: number) => {
    const sessionVersion = beginSession();
    if (!isImpersonationToken(newToken)) {
      clearImpersonationRecovery();
    }
    await commit(newToken, expiresIn, sessionVersion);
  };
}

/** Keeps one restorer per provider so concurrent restores share a pending result. */
function useRestoreImpersonationSession(options) {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = () => createImpersonationSessionRestorer(options);
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [restore] = useState(t0);
  return restore;
}